import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiPlay, FiMusic, FiRefreshCw, FiEye, FiX, FiAlertCircle } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { authAPI, mockDialoguesAPI, mockSubmissionsAPI, CategoryDialogue, MyMockSubmission, MockCreditsInfo } from '../services/api';
import MockCreditPanel from '../components/MockCreditPanel';
import { ensureMockDialogueLabels } from '../utils/mockDialogueCodes';
import '../components/Dashboard.css';
import '../components/MockCreditPanel.css';
import './PageTemplate.css';
import './MockService.css';

interface MockServiceProps {
  onLogout?: () => void;
  userEmail?: string;
}

const MockService: React.FC<MockServiceProps> = ({ onLogout, userEmail }) => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
  });
  const [mockCredits, setMockCredits] = useState<MockCreditsInfo | null>(null);
  const [mockDialogues, setMockDialogues] = useState<CategoryDialogue[]>([]);
  const [previousSubmissions, setPreviousSubmissions] = useState<MyMockSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [pendingMockDialogue, setPendingMockDialogue] = useState<CategoryDialogue | null>(null);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [mockData, submissions] = await Promise.all([
        mockDialoguesAPI.list(),
        mockSubmissionsAPI.list(),
      ]);
      setMockDialogues(mockData.dialogues);
      setMockCredits(mockData.mock_credits);
      setPreviousSubmissions(submissions);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setLoadError(err.message || 'Failed to load mock tests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await authAPI.getProfile();
        setUserInfo({
          email: profile.email || userEmail || 'anonymous.user@naati.edu',
          organization: profile.organization || 'NAATI',
          first_name: profile.first_name || '',
        });
      } catch {
        // keep defaults
      }
    };
    loadProfile();
  }, [userEmail]);

  const navigateToMock = (d: CategoryDialogue) => {
    navigate(`/material/dialogues/${d.id}?mock=1`, {
      state: { categoryDialogue: d, dialogueId: d.id, fromMockService: true },
    });
  };

  const handleStartMock = (d: CategoryDialogue) => {
    if (!mockCredits?.unlimited && !mockCredits?.can_take_mock) {
      setShowNoCreditsModal(true);
      return;
    }

    if (mockCredits?.unlimited) {
      navigateToMock(d);
      return;
    }

    setPendingMockDialogue(d);
    setShowCreditConfirm(true);
  };

  const confirmStartMock = () => {
    if (!pendingMockDialogue) return;
    navigateToMock(pendingMockDialogue);
    setShowCreditConfirm(false);
    setPendingMockDialogue(null);
  };

  const cancelStartMock = () => {
    setShowCreditConfirm(false);
    setPendingMockDialogue(null);
  };

  const creditBalanceLabel =
    mockCredits?.unlimited
      ? 'unlimited'
      : String(mockCredits?.balance ?? 0);

  const handleViewResult = (submissionId: number) => {
    navigate(`/mock-service/result/${submissionId}`);
  };

  const mockLabelById = useMemo(() => {
    const ids = [
      ...mockDialogues.map((d) => d.id),
      ...previousSubmissions.map((s) => s.dialogue_id),
    ];
    return ensureMockDialogueLabels(ids);
  }, [mockDialogues, previousSubmissions]);

  const latestSubmissionByDialogueId = useMemo(() => {
    const m = new Map<number, MyMockSubmission>();
    for (const s of previousSubmissions) {
      const cur = m.get(s.dialogue_id);
      if (!cur || new Date(s.created_at).getTime() > new Date(cur.created_at).getTime()) {
        m.set(s.dialogue_id, s);
      }
    }
    return m;
  }, [previousSubmissions]);

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mock-service-content"
            >
              <h1 className="page-title">Mock Service</h1>
              <p className="page-description">
                Practice full mock dialogue tests in exam-style conditions. Each test uses{' '}
                <strong>one mock credit</strong> — separate from your subscription. After each dialogue
                segment, the system pauses briefly and then starts recording automatically so you can focus
                on your interpretation.
              </p>

              <MockCreditPanel creditsInfo={mockCredits} onCreditsChange={fetchAll} />

              {loading ? (
                <div className="mock-loading">
                  {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                  <p>Loading mock tests...</p>
                </div>
              ) : loadError ? (
                <div className="mock-error">
                  <p>{loadError}</p>
                  <button onClick={fetchAll} className="retry-btn">Retry</button>
                </div>
              ) : (
                <>
                  <section className="mock-section">
                    <h2 className="mock-section-title">Start a new mock test</h2>
                    {mockDialogues.length === 0 ? (
                      <p className="mock-empty-text">No mock tests are available right now. Please check back later.</p>
                    ) : (
                      <div className="mock-topics-grid">
                        {mockDialogues.map((d) => {
                          const sub = latestSubmissionByDialogueId.get(d.id);
                          const done = Boolean(d.user_has_mock_submission) || sub != null;
                          const overallScore =
                            typeof d.user_mock_overall_score === 'number' &&
                            !Number.isNaN(d.user_mock_overall_score)
                              ? d.user_mock_overall_score
                              : sub?.status === 'graded' && typeof sub.overall_score === 'number'
                                ? sub.overall_score
                                : null;
                          return (
                            <motion.div
                              key={d.id}
                              className={`mock-topic-card${done ? ' mock-topic-card-done' : ''}`}
                              whileHover={
                                done
                                  ? undefined
                                  : { y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }
                              }
                              onClick={() => {
                                if (done) {
                                  if (sub?.status === 'graded') {
                                    handleViewResult(sub.id);
                                  }
                                  return;
                                }
                                handleStartMock(d);
                              }}
                            >
                              <div className="mock-topic-icon">
                                {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 24 })}
                              </div>
                              <h3 className="mock-topic-name">{mockLabelById.get(d.id) ?? '—'}</h3>
                              {done ? (
                                <span className="mock-topic-completed-badge" aria-label="Mock already submitted">
                                  Completed
                                </span>
                              ) : null}
                              <span className="mock-topic-audios">{d.audio_count} audios</span>
                              {done ? (
                                sub?.status === 'graded' ? (
                                  <button
                                    type="button"
                                    className="mock-topic-btn mock-topic-btn-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewResult(sub.id);
                                    }}
                                  >
                                    {React.createElement(FiEye as React.ComponentType<{ size?: number }>, { size: 18 })}
                                    View result
                                  </button>
                                ) : (
                                  <p className="mock-pending-label mock-topic-pending-inline">
                                    Submitted — awaiting grading
                                  </p>
                                )
                              ) : (
                                <button
                                  type="button"
                                  className="mock-topic-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartMock(d);
                                  }}
                                >
                                  {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                                  Start Mock Test
                                </button>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  <section className="mock-section">
                    <h2 className="mock-section-title">Previous Tests</h2>
                    {previousSubmissions.length === 0 ? (
                      <p className="mock-empty-text">You have not submitted any mock tests yet.</p>
                    ) : (
                      <div className="mock-previous-table-wrap">
                        <table className="mock-previous-table">
                          <thead>
                            <tr>
                              <th>Test</th>
                              <th>Status</th>
                              <th>Date</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {previousSubmissions.map((s) => (
                              <tr key={s.id}>
                                <td>{mockLabelById.get(s.dialogue_id) ?? '—'}</td>
                                <td>
                                  <span className={`mock-status mock-status-${s.status}`}>
                                    {s.status}
                                  </span>
                                </td>
                                <td className="mock-previous-overall-cell">
                                  {s.status === 'graded' && typeof s.overall_score === 'number'
                                    ? `${s.overall_score.toFixed(2)} / 5`
                                    : '—'}
                                </td>
                                <td>{new Date(s.created_at).toLocaleDateString()}</td>
                                <td>
                                  {s.status === 'graded' ? (
                                    <button
                                      type="button"
                                      className="mock-view-result-btn"
                                      onClick={() => handleViewResult(s.id)}
                                    >
                                      {React.createElement(FiEye as React.ComponentType<{ size?: number }>, { size: 16 })}
                                      View result
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showNoCreditsModal ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowNoCreditsModal(false)}
          >
            <motion.div
              className="mock-credits-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>No mock credits</h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setShowNoCreditsModal(false)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <div className="mock-no-credits-icon">
                  {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 40 })}
                </div>
                <p className="mock-confirm-text">
                  You don&apos;t have any mock credits. Purchase credits using the panel above, then return here to
                  start your test.
                </p>
              </div>
              <div className="mock-credits-modal-footer">
                <button
                  type="button"
                  className="mock-credits-btn mock-credits-btn-primary"
                  onClick={() => setShowNoCreditsModal(false)}
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}

        {showCreditConfirm && pendingMockDialogue ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={cancelStartMock}
          >
            <motion.div
              className="mock-credits-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>Use 1 mock credit?</h3>
                <button type="button" className="mock-credits-modal-close" onClick={cancelStartMock}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p className="mock-confirm-text">
                  Starting <strong>{mockLabelById.get(pendingMockDialogue.id) ?? 'this mock test'}</strong> will
                  use <strong>1 mock credit</strong> from your balance.
                </p>
                <p className="mock-confirm-text mock-confirm-balance">
                  Your current balance: <strong>{creditBalanceLabel}</strong> credit
                  {mockCredits?.balance === 1 ? '' : 's'}
                </p>
                <p className="mock-confirm-text mock-confirm-note">
                  Once you begin, the credit is used when you submit the test. Make sure you are ready before continuing.
                </p>
              </div>
              <div className="mock-credits-modal-footer">
                <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={cancelStartMock}>
                  Cancel
                </button>
                <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={confirmStartMock}>
                  {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 16 })}
                  Start test
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default MockService;
